"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AudioPlaybackServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
class AudioPlaybackService extends client_1.RWSService {
    constructor() {
        super(...arguments);
        this.currentAudioElement = null;
        this.volume = 100;
    }
    /**
     * Play audio from URL or Blob
     */
    async playAudio(audioSource, options = {}) {
        var _a;
        try {
            console.log('[AudioPlaybackService] playAudio', {
                isBlob: audioSource instanceof Blob,
                autoPlay: options.autoPlay
            });
            // Stop current audio if playing
            this.stopAudio();
            const audioUrl = audioSource instanceof Blob
                ? URL.createObjectURL(audioSource)
                : audioSource;
            this.currentAudioElement = new Audio(audioUrl);
            this.currentAudioElement.volume = this.volume / 100;
            this.currentAudioElement.onplay = () => {
                var _a;
                console.log('[AudioPlaybackService] onplay');
                (_a = options.onPlay) === null || _a === void 0 ? void 0 : _a.call(options);
            };
            this.currentAudioElement.onpause = () => {
                var _a;
                console.log('[AudioPlaybackService] onpause');
                (_a = options.onPause) === null || _a === void 0 ? void 0 : _a.call(options);
            };
            this.currentAudioElement.onended = () => {
                var _a;
                console.log('[AudioPlaybackService] onended');
                (_a = options.onEnd) === null || _a === void 0 ? void 0 : _a.call(options);
                this.currentAudioElement = null;
            };
            this.currentAudioElement.onerror = (event) => {
                var _a, _b;
                console.error('[AudioPlaybackService] onerror', event, (_a = this.currentAudioElement) === null || _a === void 0 ? void 0 : _a.error);
                const error = new Error('Audio playback error');
                (_b = options.onError) === null || _b === void 0 ? void 0 : _b.call(options, error);
            };
            if (options.autoPlay !== false) {
                await this.currentAudioElement.play();
                console.log('[AudioPlaybackService] play() resolved');
            }
            return true;
        }
        catch (error) {
            console.error('Failed to play audio:', error);
            (_a = options.onError) === null || _a === void 0 ? void 0 : _a.call(options, error);
            return false;
        }
    }
    /**
     * Stop current audio playback
     */
    stopAudio() {
        if (this.currentAudioElement) {
            this.currentAudioElement.pause();
            this.currentAudioElement.currentTime = 0;
            this.currentAudioElement = null;
        }
    }
    /**
     * Play streamed audio from ReadableStream
     */
    async playStreamedAudio(stream) {
        try {
            const audioContext = new AudioContext();
            const reader = stream.getReader();
            const chunks = [];
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                chunks.push(value);
            }
            // Combine all chunks
            const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
            const combined = new Uint8Array(totalLength);
            let offset = 0;
            for (const chunk of chunks) {
                combined.set(chunk, offset);
                offset += chunk.length;
            }
            // Decode and play
            const audioBuffer = await audioContext.decodeAudioData(combined.buffer);
            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            source.start();
        }
        catch (error) {
            console.error('Failed to play streamed audio:', error);
        }
    }
    /**
     * Get current playback state
     */
    getPlaybackState() {
        if (!this.currentAudioElement) {
            return {
                isPlaying: false,
                currentTime: 0,
                duration: 0,
                volume: this.volume
            };
        }
        return {
            isPlaying: !this.currentAudioElement.paused,
            currentTime: this.currentAudioElement.currentTime,
            duration: this.currentAudioElement.duration || 0,
            volume: this.volume
        };
    }
    /**
     * Set playback volume (0-100), applied to current and future audio
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(100, Number(volume) || 0));
        if (this.currentAudioElement) {
            this.currentAudioElement.volume = this.volume / 100;
        }
    }
    /**
     * Get current volume (0-100)
     */
    getVolume() {
        return this.volume;
    }
    /**
     * Seek to specific time
     */
    seekTo(time) {
        if (this.currentAudioElement) {
            this.currentAudioElement.currentTime = time;
        }
    }
    /**
     * Pause current audio
     */
    pauseAudio() {
        if (this.currentAudioElement && !this.currentAudioElement.paused) {
            this.currentAudioElement.pause();
        }
    }
    /**
     * Resume current audio
     */
    async resumeAudio() {
        if (this.currentAudioElement && this.currentAudioElement.paused) {
            try {
                await this.currentAudioElement.play();
                return true;
            }
            catch (error) {
                console.error('Failed to resume audio:', error);
                return false;
            }
        }
        return false;
    }
    /**
     * Clean up all resources
     */
    destroy() {
        this.stopAudio();
    }
}
exports.AudioPlaybackServiceInstance = AudioPlaybackService;
exports.default = AudioPlaybackService.getSingleton();
